/**
 * @fileoverview Sandbox Platform SDK - Main exports
 *
 * A simple, powerful SDK for creating and managing cloud development environments.
 * Inspired by E2B's approach but built for the Sandbox Platform.
 *
 * @example
 * ```typescript
 * import { Sandbox, TechStack } from '@teamnhz/sandbox-sdk'
 *
 * // Create a sandbox
 * const sandbox = await Sandbox.create({
 *   techStack: TechStack.NODEJS,
 *   apiKey: 'sbp_your_api_key'
 * })
 *
 * // Run code
 * const result = await sandbox.runCode('console.log("Hello World")')
 * console.log(result.output) // "Hello World"
 *
 * // Cleanup
 * await sandbox.close()
 * ```
 */

// Main Sandbox class
export { Sandbox } from './sandbox'
import { Sandbox } from './sandbox'

// HTTP and WebSocket clients
export { HttpClient, WebSocketClient } from './client'

// Authentication
export { AuthManager, createAuthManager, isApiKey, isJwtToken, detectAuthType } from './auth'

// File System API (WebContainer-style)
export type {
  FileSystemAPI,
  FileSystemTree,
  FileNode,
  FileStats,
  Dirent,
  FileSystemWatcher
} from './filesystem'
export { SandboxFileSystem } from './filesystem'

// Process Management API
export type {
  SandboxProcess,
  SpawnOptions,
  TerminalSize,
  ProcessExit,
  WritableStream,
  ReadableStream
} from './process'
export { ProcessManager } from './process'

// Preview System with Error Capture
export type {
  PreviewScriptOptions,
  PreviewError,
  PortChangeEvent
} from './preview'
export { PreviewManager } from './preview'

// Hot Module Reload (HMR) System
export type {
  HMROptions,
  HMRUpdate,
  HMRStateSnapshot
} from './hmr'
export { HMRManager } from './hmr'

// JSH Shell with OSC Sequences
export type {
  JSHResult,
  OSCCommand,
  JSHOptions
} from './jsh'
export { JSHShell, OSCSequence, OSCUtils } from './jsh'

// Full-text Search Engine
export type {
  SearchOptions,
  SearchResult,
  SearchProgress,
  SearchSummary
} from './search'
export { SearchEngine } from './search'

// Multi-tab Synchronization
export type {
  SyncOptions,
  SyncMessage,
  FileSyncChange,
  CursorSyncInfo,
  TabInfo
} from './sync'
export { MultiTabSyncManager } from './sync'

// Types and interfaces
export type {
  // Core types
  SandboxConfig,
  CreateSandboxOptions,
  Sandbox as SandboxInfo,
  SandboxUrl,

  // Execution
  ExecutionResult,
  ExecuteCodeOptions,
  StreamExecutionOptions,

  // File operations
  FileInfo,
  FileOperationOptions,
  WatchOptions,
  FileChangeEvent,
  OperationProgress,
  BulkOperationOptions,

  // Authentication
  AuthConfig,
  SandboxClientConfig,

  // API responses
  ApiResponse,
  ApiError,

  // WebSocket
  WebSocketMessage,

  // Job management
  JobProgress
} from './types'

// Enums
export {
  TechStack,
  SandboxStatus,
  WebSocketEventType
} from './types'

// Error classes
export {
  // Base error
  SandboxPlatformError,

  // Specific errors
  AuthenticationError,
  NetworkError,
  SandboxError,
  FileError,
  ValidationError,
  RateLimitError,
  WebSocketError,

  // Utilities
  createErrorFromResponse,
  isSandboxPlatformError
} from './errors'

// Default export for convenience
export default Sandbox